import React, { useCallback, useEffect, useRef, useState } from "react";
import * as api from "./electricalApi";

/**
 * The drafting tab. Ported from Csa/CsaDraft.js.
 *
 * The screen exists to answer one question: **which of these do I have to
 * read?** Nobody reads every finding, so the ordering is the feature. The
 * server returns disagreements first; this renders them first and says how
 * many there are before anything else.
 *
 * Three states, and they look different on purpose:
 *
 *   nothing yet   a button, and what it will cost in time
 *   running       progress, and a way to stop
 *   done          the drafts, flagged ones at the top
 *
 * Nothing here writes to the report. Accepting does, and accepting is always
 * a deliberate click.
 *
 * Field names differ from CSA's: `checkpoint_code` not `question_code`.
 * The auditor's own text comes back as `auditor_comment` (see `auditorText`
 * below), even though Electrical's own answer field is `elec_answer.observation`
 * — the drafting API apparently names it after CSA's convention regardless.
 * There is also no "element" tier — only area and category — so the
 * per-item label shows category, falling back to area.
 */

const POLL_MS = 2000;

/** Statuses that mean the job is still going. */
const LIVE = ["queued", "running", "paused"];

/**
 * The recommendation shown for an item: whatever is already saved on the
 * checkpoint's own answer, else the drafted suggestion (`draft_recommendation`,
 * from the same job that wrote `draft_text`), else the checklist's own
 * wording. Any of the three can be missing - a checkpoint already answered
 * Yes, or one the model skipped, may have none at all.
 */
function itemRecommendation(item, answers, checklistRecommendations) {
  const saved = answers && answers[item.checkpoint_code];
  const checklistRec = (checklistRecommendations && checklistRecommendations[item.checkpoint_code]) || null;
  return (saved && saved.recommendation) || item.draft_recommendation || checklistRec || null;
}

/*
 * The real draftItems() response carries this as `auditor_comment`, not
 * `auditor_observation` - this file's own header comment names
 * `auditor_observation` as Electrical's field (vs CSA's `auditor_comment`),
 * which held for the answer itself but not, it turns out, for this one
 * response. Both are checked so either shape is read correctly.
 */
const auditorText = (item) => item.auditor_comment || item.auditor_observation || null;

export default function ElectricalDraft({ auditId, audit, answers, checklistRecommendations, onAccepted }) {
  const [progress, setProgress] = useState(null);
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const timer = useRef(null);

  const submitted = audit && audit.status !== "draft";

  const loadProgress = useCallback(async () => {
    try {
      const r = await api.draftProgress(auditId);
      setProgress(r.progress);
      return r.progress;
    } catch (e) {
      setError(e.message || "Could not read progress");
      return null;
    }
  }, [auditId]);

  const loadItems = useCallback(async () => {
    try {
      const r = await api.draftItems(auditId);
      setItems(r.items || []);
    } catch (e) {
      setError(e.message || "Could not read the drafts");
    }
  }, [auditId]);

  /* Poll only while the job is live; stop as soon as it is not. */
  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      const p = await loadProgress();
      if (cancelled) return;
      if (p && LIVE.includes(p.status)) {
        timer.current = setTimeout(tick, POLL_MS);
      } else if (p && p.status !== "none") {
        loadItems();
      }
    };

    tick();
    return () => {
      cancelled = true;
      clearTimeout(timer.current);
    };
  }, [loadProgress, loadItems]);

  const start = async () => {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const r = await api.startDraft(auditId);
      setNote(r.message);
      const p = await loadProgress();
      if (p && LIVE.includes(p.status)) timer.current = setTimeout(async () => {
        const next = await loadProgress();
        if (next && !LIVE.includes(next.status)) loadItems();
      }, POLL_MS);
    } catch (e) {
      /*
       * The refusals are the informative ones — drafting switched off, not
       * configured, daily limit reached. They arrive as a message written for
       * a person, so show it rather than a generic failure.
       */
      setError(e.message || "Could not start drafting");
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    setBusy(true);
    try {
      const r = await api.cancelDraft(auditId);
      setNote(r.message);
      await loadProgress();
      await loadItems();
    } catch (e) {
      setError(e.message || "Could not stop drafting");
    } finally {
      setBusy(false);
    }
  };

  /*
   * `recommendations` is {checkpoint_code: text} - for a single item, the
   * text currently selected in its dropdown; acceptDraft takes it straight
   * on the body now, so "Use this" is one request instead of a draft-accept
   * plus a separate saveAnswer. Bulk ("Accept all") has no single
   * checkpoint_code to key off, so it rides through with no recommendation
   * override - every item there was already shown its own on screen before
   * the auditor chose to accept all of them together.
   */
  const accept = async (body, recommendations) => {
    setBusy(true);
    setError(null);
    try {
      if (recommendations) body.recommendation = recommendations[body.checkpoint_code];
      const r = await api.acceptDraft(auditId, body);
      setNote(r.message);
      await loadItems();
      console.log("accept: calling onAccepted?", typeof onAccepted);
      if (onAccepted) await onAccepted();
    } catch (e) {
      setError(e.message || "Could not accept");
    } finally {
      setBusy(false);
    }
  };

  if (!submitted) {
    return (
      <div className="dr-empty">
        <h3>Nothing to draft yet</h3>
        <p>
          Drafting runs on a submitted audit. It writes from the observations
          an auditor typed, so it has nothing to work from until the audit is
          finished.
        </p>
      </div>
    );
  }

  const live = progress && LIVE.includes(progress.status);
  const drafted = (items || []).filter(
    (i) => (i.status === "drafted" || i.status === "reused") && !i.accepted_at
  );
  const flagged = drafted.filter((i) => i.agreed === 0);
  /*
   * Drafted, but never compared — the second pass failed, usually a dropped
   * connection. Without this group these would fall through both filters
   * and appear nowhere.
   */
  const unchecked = drafted.filter((i) => i.agreed === null || i.agreed === undefined);
  const clean = drafted.filter((i) => i.agreed === 1);
  const skipped = (items || []).filter((i) => i.status === "skipped");

  return (
    <div className="dr">
      {error ? <div className="dr-error">{error}</div> : null}
      {note ? <div className="dr-note">{note}</div> : null}

      <Head
        progress={progress}
        live={live}
        busy={busy}
        onStart={start}
        onStop={stop}
      />

      {live ? <Bar progress={progress} /> : null}

      {!live && items && items.length ? (
        <>
          {flagged.length ? (
            <Group
              tone="flag"
              title={`${flagged.length} need a person`}
              blurb="Each of these was drafted twice and the two drafts disagreed on a fact — a number or a name. That is the signal a detail was invented rather than read. Both versions are shown; neither is accepted until you say so."
            >
              {flagged.map((i) => (
                <Item
                  key={i.checkpoint_code}
                  item={i}
                  answers={answers}
                  checklistRecommendations={checklistRecommendations}
                  busy={busy}
                  onAccept={accept}
                  showBoth
                />
              ))}
            </Group>
          ) : null}

          {unchecked.length ? (
            <Group
              tone="flag"
              title={`${unchecked.length} not checked`}
              blurb="These were drafted once, but the second pass did not complete — usually a dropped connection — so nothing was compared. A check that did not run is not a check that passed. Read these, or run drafting again to compare them."
            >
              {unchecked.map((i) => (
                <Item
                  key={i.checkpoint_code}
                  item={i}
                  answers={answers}
                  checklistRecommendations={checklistRecommendations}
                  busy={busy}
                  onAccept={accept}
                />
              ))}
            </Group>
          ) : null}

          {clean.length ? (
            <Group
              tone="ok"
              title={`${clean.length} ready`}
              blurb="Both drafts of each of these agreed on every fact. Accepting them together skips anything above."
              action={
                <button
                  type="button"
                  className="cap-btn"
                  disabled={busy}
                  onClick={() => {
                    // Same recommendation each item already shows on screen.
                    const recommendations = {};
                    clean.forEach((i) => {
                      recommendations[i.checkpoint_code] = itemRecommendation(i, answers, checklistRecommendations);
                    });
                    accept({ all: true }, recommendations);
                  }}
                >
                  Accept all {clean.length}
                </button>
              }
            >
              {clean.map((i) => (
                <Item
                  key={i.checkpoint_code}
                  item={i}
                  answers={answers}
                  checklistRecommendations={checklistRecommendations}
                  busy={busy}
                  onAccept={accept}
                />
              ))}
            </Group>
          ) : null}

          {skipped.length ? (
            <Group
              tone="skip"
              title={`${skipped.length} skipped`}
              blurb="No draft was written for these, and nothing was sent to the model. Write a fuller observation on the audit and run drafting again."
            >
              {skipped.map((i) => (
                <Skipped key={i.checkpoint_code} item={i} />
              ))}
            </Group>
          ) : null}
        </>
      ) : null}

      {!live && items && !items.length && progress && progress.status !== "none" ? (
        <p className="dr-blurb">That run produced nothing. Check the skipped reasons above.</p>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function Head({ progress, live, busy, onStart, onStop }) {
  const never = !progress || progress.status === "none";

  return (
    <div className="dr-head">
      <div>
        <h3>Draft the findings</h3>
        <p className="dr-blurb">
          {never
            ? "Writes one sentence per Partial and No answer, from the observation the auditor typed. Each is drafted twice and the two are compared — where they disagree on a fact, you are told."
            : statusLine(progress)}
        </p>
      </div>
      <div className="dr-actions">
        {live ? (
          <button type="button" className="small-btn" disabled={busy} onClick={onStop}>
            Stop
          </button>
        ) : (
          <button type="button" className="cap-btn" disabled={busy} onClick={onStart}>
            {never ? "Draft" : "Draft again"}
          </button>
        )}
      </div>
    </div>
  );
}

function statusLine(p) {
  if (p.status === "paused") return p.reason || "Paused. It will carry on tomorrow.";
  if (p.status === "cancelled") return "Stopped. The drafts already written were kept.";
  if (p.status === "failed") return p.reason || "That run failed.";
  if (p.status === "running" || p.status === "queued") return "Working…";
  const bits = [];
  if (p.drafted) bits.push(`${p.drafted} drafted`);
  if (p.reused) bits.push(`${p.reused} unchanged`);
  if (p.skipped) bits.push(`${p.skipped} skipped`);
  if (p.failed) bits.push(`${p.failed} failed`);
  return (p.reason ? p.reason + " · " : "") + (bits.join(" · ") || "Nothing to draft.");
}

function Bar({ progress }) {
  const total = Math.max(1, progress.total || 1);
  const pct = Math.min(100, Math.round(((progress.done || 0) / total) * 100));
  return (
    <div className="dr-bar-wrap">
      <div className="dr-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <span style={{ width: pct + "%" }} />
      </div>
      <p className="dr-bar-label">
        {progress.done || 0} of {progress.total || 0}
        {progress.status === "paused" ? " · paused" : ""}
      </p>
    </div>
  );
}

function Group({ tone, title, blurb, action, children }) {
  return (
    <section className={"dr-group " + tone}>
      <div className="dr-group-head">
        <div>
          <h4>{title}</h4>
          <p className="dr-blurb">{blurb}</p>
        </div>
        {action || null}
      </div>
      {children}
    </section>
  );
}

/**
 * The dropdown below is the choice of what becomes this checkpoint's
 * recommendation: what the auditor wrote, the model's recommendation
 * (whatever is already saved, else the draft, else the checklist's own
 * wording), or the second draft, whichever apply. Auditor text is the
 * default, since it is never wrong about what the auditor meant to say.
 *
 * It's a <details>, not a native <select>: Chrome/Edge render a <select>'s
 * open list as an OS-level popup that ignores white-space/overflow-wrap on
 * <option>, so long text widens the popup past the closed box rather than
 * wrapping. <details> is plain DOM content, so it wraps and sizes like
 * everything else on the page.
 *
 * "Use this" accepts the drafted text into the report (there is no
 * server-side way to accept a draft but keep different observation wording
 * instead) and sets the answer's recommendation to whatever the dropdown is
 * currently sitting on, in the same request (accept() above).
 */
function Item({ item, answers, checklistRecommendations, busy, onAccept, showBoth }) {
  const [open, setOpen] = useState(false);
  const accepted = !!item.accepted_at;
  const detailsRef = useRef(null);

  const recommendation = itemRecommendation(item, answers, checklistRecommendations);
  const auditorSaid = auditorText(item);

  const options = [];
  if (auditorSaid) options.push({ key: "auditor", label: "Auditor wrote", text: auditorSaid });
  if (recommendation) options.push({ key: "recommendation", label: "Recommendation", text: recommendation });
  if (showBoth && item.alt_text) options.push({ key: "alt", label: "Second draft", text: item.alt_text });

  const [choice, setChoice] = useState(options[0] ? options[0].key : null);
  const current = options.find((o) => o.key === choice) || options[0];

  const pick = (o) => {
    setChoice(o.key);
    if (detailsRef.current) detailsRef.current.open = false;
  };

  return (
    <article className={"dr-item" + (accepted ? " done" : "")}>
      <header>
        <code>{item.checkpoint_code}</code>
        <span className="dr-el">{item.category || item.area}</span>
        {accepted ? <span className="dr-tag ok">In the report</span> : null}
      </header>

      {/* {auditorSaid ? (
        <p className="dr-said">
          <span>Auditor wrote</span>
          {auditorSaid}
        </p>
      ) : null} */}

      {/* {showBoth && item.alt_text ? (
        <>
          <button type="button" className="dr-link" onClick={() => setOpen(!open)}>
            {open ? "Hide" : "Show"} the second draft
          </button>
          {open ? <p className="dr-draft alt">{item.alt_text}</p> : null}
        </>
      ) : null} */}

      {/* {recommendation ? (
        <div className="dr-rec">
          <span className="dr-rec-label">Recommendation</span>
          <p className="dr-rec-text">{recommendation}</p>
        </div>
      ) : null} */}

      {options.length ? (
        <details className="dr-select" ref={detailsRef}>
          <summary>{current ? current.text : "Choose"}</summary>
          <div className="dr-select-list">
            {options.map((o) => (
              <button
                key={o.key}
                type="button"
                className={"dr-select-opt" + (current && current.key === o.key ? " on" : "")}
                onClick={() => pick(o)}
              >
                <span className="dr-select-opt-label">{o.label}</span>
                {o.text}
              </button>
            ))}
          </div>
        </details>
      ) : null}

      {!accepted ? (
        <div className="dr-item-actions">
          <button
            type="button"
            className="small-btn"
            disabled={busy}
            onClick={() =>
              onAccept(
                { checkpoint_code: item.checkpoint_code },
                current ? { [item.checkpoint_code]: current.text } : undefined
              )
            }
          >
            Use this
          </button>
        </div>
      ) : null}
    </article>
  );
}

function Skipped({ item }) {
  const auditorSaid = auditorText(item);
  return (
    <article className="dr-item skip">
      <header>
        <code>{item.checkpoint_code}</code>
        <span className="dr-el">{item.category || item.area}</span>
      </header>
      <p className="dr-why">{item.skip_reason}</p>
      {auditorSaid ? (
        <p className="dr-said">
          <span>Auditor wrote</span>
          {auditorSaid}
        </p>
      ) : null}
    </article>
  );
}
